// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import PostalCode from "@bg/App/Components/PostalCode";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { screen } from "@bg/XDL/Helpers";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { styledScrollbar } from "@bg/XDL/Utilities";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import setFlyoutDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetFlyoutDrawerIsOpen";
import setInitialValues from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetInitialValues";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import {
    getCurrencies,
    getFulfillmentLabel,
    getIsPunchOutSession,
    getLanguages,
    getSession,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setCurrency from "@insite/client-framework/Store/Context/Handlers/SetCurrency";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import {
    getCategoriesDataView,
    getCategoryState,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { getHeader, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import {
    getPageLinkByNodeId,
    getPageLinkByPageType,
    LinkModel,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { BrandModel } from "@insite/client-framework/Types/ApiModels";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import ShowInventoryToggle, { ShowInventoryToggleStyles } from "@insite/content-library/Components/ShowInventoryToggle";
import ShowPricingToggle, { ShowPricingToggleStyles } from "@insite/content-library/Components/ShowPricingToggle";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconProps } from "@insite/mobius/Icon";
import { MappedLink } from "@insite/mobius/Menu";
import PanelMenu, { PanelMenuPresentationProps } from "@insite/mobius/PanelMenu";
import PanelRow, { PanelRowPresentationProps } from "@insite/mobius/PanelMenu/PanelRow";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omitSingle from "@insite/mobius/utilities/omitSingle";
import React from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const headerLinkListLinkFields =
        (getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
            return widget.type === "Header/HeaderLinkList";
        })?.fields.links as LinkModel[]) ?? [];
    const displayPricingAndInventory = state.context.session?.displayPricingAndInventory || false;
    const currentMode = getCookie(NavigationModeCookieName) || "Storefront";

    const headerShopByCatLinks =
        (getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
            return widget.type === "ShopByCategoryL1L2/ShopByCategoryL1L2";
        })?.fields.l1Links as any[]) ?? [];

    return {
        currencies: getCurrencies(state),
        currentCurrencyId: state.context.session.currency?.id,
        currentCurrencySymbol: state.context.session.currency?.currencySymbol,
        languages: getLanguages(state),
        currentLanguage: state.context.session.language,
        currentLocation: getLocation(state),
        isSigningIn: state.context.isSigningIn,
        userName: state.context.session?.userName,
        isGuest: state.context.session?.isGuest,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage"),
        headerLinkListLinkFields,
        showCustomerMenuItem: getSettingsCollection(state).accountSettings.enableWarehousePickup,
        fulfillmentLabel: getFulfillmentLabel(state),
        navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        flyoutDrawerIsOpen: state.components.addressDrawer.flyoutDrawerIsOpen,
        isPunchOutSession: getIsPunchOutSession(state),
        visibilityState: findVisibilityState(state.data.pages.widgetsById),
        displayPricingAndInventoryToggles: displayPricingAndInventory && currentMode === "Storefront",
        headerShopByCatLinks,
        categoriesPageUrl: getPageLinkByPageType(state, "CategoryListPage")?.url,
        fulfillmentMethod: getSession(state).fulfillmentMethod,
        isAuth: getSession(state).isAuthenticated,
        state,
        categoriesDataView: getCategoriesDataView(state),
        categories: state.xHeader.headerLinks,
    };
};

const mapDispatchToProps = {
    setCurrency,
    setLanguage,
    signOut,
    cancelPunchOut,
    setInitialValues,
    setNavDrawerIsOpen,
    setFlyoutDrawerIsOpen,
    setSignInOverlayIsOpen,
    loadCategories,
};

interface OwnProps {
    links: MappedLink[];
    showQuickOrder: boolean;
    showShopByCategory: boolean;
    showBrandsAsOverlay: boolean;
    quickOrderLink: PageLinkModel | undefined;
    brandsPageUrl: PageLinkModel | undefined;
    featuredBrands?: BrandModel[];
    isCompactHeaderFlyout?: boolean;
    showFlyoutPricingToggle?: boolean;
    showFlyoutInventoryToggle?: boolean;
    rootElementSelector?: string;
    allowOpenParentLinks?: boolean;
}

type Props = OwnProps &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

export interface NavigationDrawerStyles {
    menuTriggerButton?: ButtonPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerSectionWrapper?: InjectableCss;
    currencySelectorWrapper?: InjectableCss;
    languageSelectorWrapper?: InjectableCss;
    panelMenu?: PanelMenuPresentationProps;
    panelSectionWrapper?: InjectableCss;
    menuRowIcon?: IconProps;
    userRowTypography?: TypographyPresentationProps;
    punchOutUserRowText?: TypographyPresentationProps;
    mainNavigationRow?: PanelRowPresentationProps;
    headerShipToAddressStyles?: PanelRowPresentationProps;
    mainNavigationRowIcon?: IconProps;
    punchOutNavigationRowIcon?: IconProps;
    mainNavigationRowTypography?: TypographyPresentationProps;
    logoLinks?: PanelRowPresentationProps & { typographyProps: TypographyPresentationProps };
    currencySymbol?: TypographyPresentationProps;
    changeCustomerRow?: PanelRowPresentationProps;
    changeCustomerRowContainer?: GridContainerProps;
    fulfillmentMethodGridItem?: GridItemProps;
    addressesGridItem?: GridItemProps;
    pickUpAddressGridItem?: GridItemProps;
    showInventoryToggle?: ShowInventoryToggleStyles;
    showPricingToggle?: ShowPricingToggleStyles;
    applyButtonGridItem?: GridItemProps;
}

const StyledSection = getStyledWrapper("section");
const StyledSpan = getStyledWrapper("span");

interface CustomLinkModel {
    url: string | undefined;
    title: string | undefined;
    overrideTitleL2?: string;
}

interface CategoryModel {
    link: CustomLinkModel;
    children: CustomLinkModel[];
    overrideTitle: string;
}

export const navigationDrawerStyles: NavigationDrawerStyles = {
    menuTriggerButton: {
        shape: "pill",
        color: "secondary",
        size: 50,
        css: css`
            &:focus {
                outline: none;
            }
        `,
    },
    drawer: {
        size: 300,
        cssOverrides: {
            drawerContent: css`
                margin-top: -2px;
                overflow-x: hidden;
                overflow-y: auto;
                ${styledScrollbar(5)}
            `,
            scrim: css`
                top: 90px;
                ${screen("lte", "md")`          
                    top: 141px;
                `}
            `,
            drawerTitle: css`
                display: none;
            `,
        },
    },
    drawerSectionWrapper: {
        css: css``,
    },
    panelMenu: {
        cssOverrides: {
            wrapper: css`
                text-align: left;
            `,
        },
    },
    panelSectionWrapper: {
        css: css`
            height: 22px;
            display: flex;
            align-items: center;
        `,
    },
    menuRowIcon: {
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    userRowTypography: {
        transform: "uppercase",
        color: "common.background",
    },
    punchOutUserRowText: {
        transform: "uppercase",
        color: "common.background",
    },
    mainNavigationRow: {
        css: css`
            background: ${getColor("common.white")};
            letter-spacing: 0;
            padding: 21px 15px 21px 15px;
            border-bottom: 1px solid ${getColor("common.ui3")};
            width: 100%;

            & {
                width: 100%;
            }

            &,
            span {
                font-size: ${fontConfig.fontSize.large}px;
                font-weight: ${fontConfig.weight.semiBold};
                text-transform: capitalize;
                color: ${getColor("secondary")};
                line-height: ${fontConfig.lineHeight.small};
                background-color: ${getColor("common.white")};
            }
        `,
    },
    headerShipToAddressStyles: {
        css: css`
            background: ${getColor("common.white")};
            letter-spacing: 0;
            padding: 21px 15px 21px 15px;
            border-bottom: 1px solid ${getColor("common.ui3")};
            width: 100%;

            & {
                width: 100%;
            }

            &,
            span,
            a {
                font-size: ${fontConfig.fontSize.large}px !important;
                font-weight: ${fontConfig.weight.semiBold} !important;
                text-transform: capitalize;
                color: ${getColor("secondary")} !important;
                line-height: ${fontConfig.lineHeight.small} !important;
                background-color: ${getColor("common.white")};
                text-decoration: none !important;
                white-space: pre-wrap;
            }

            div {
                align-items: flex-start;
            }

            .name-phone-wrapper {
                display: flex;
                flex-direction: column;

                a span {
                    display: none;
                }
            }
        `,
    },
    mainNavigationRowIcon: {
        src: "User",
        color: "common.background",
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    punchOutNavigationRowIcon: {
        src: "User",
        color: "common.background",
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    mainNavigationRowTypography: {},
    logoLinks: {
        typographyProps: {
            ellipsis: true,
        },
        css: css`
            background: ${getColor("common.white")};
            letter-spacing: 0;
            padding: 21px 15px 21px 15px;
            border-bottom: 1px solid ${getColor("common.ui3")};

            &,
            span {
                font-size: ${fontConfig.fontSize.large}px;
                font-weight: ${fontConfig.weight.semiBold};
                text-transform: capitalize;
                color: ${getColor("secondary")};
                line-height: ${fontConfig.lineHeight.small};
            }
        `,
    },
    currencySymbol: {
        size: 22,
        css: css`
            margin-top: -4px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    changeCustomerRow: {
        css: css`
            &:focus {
                outline: none;
            }
        `,
    },
    changeCustomerRowContainer: { gap: 15 },
    fulfillmentMethodGridItem: { width: 12 },
    addressesGridItem: { width: 12 },
    pickUpAddressGridItem: { width: 12 },
    applyButtonGridItem: { width: 12 },
    showInventoryToggle: {
        toggleClickable: {
            css: css`
                text-transform: uppercase;
                padding: 10px 10px 10px 20px;
            `,
        },
    },
    showPricingToggle: {
        toggleClickable: {
            css: css`
                text-transform: uppercase;
                padding: 10px 10px 10px 20px;
            `,
        },
    },
};

const styles = navigationDrawerStyles;

const NavigationDrawer = ({
    quickOrderLink,
    links,
    showQuickOrder,
    showShopByCategory,
    showBrandsAsOverlay,
    currencies,
    currentCurrencyId,
    currentCurrencySymbol,
    languages,
    currentLanguage,
    setLanguage,
    setCurrency,
    setNavDrawerIsOpen,
    setFlyoutDrawerIsOpen,
    currentLocation,
    headerLinkListLinkFields,
    headerShopByCatLinks,
    categoriesPageUrl,
    featuredBrands,
    fulfillmentMethod,
    isAuth,
    state,
    brandsPageUrl,
    categoriesDataView,
    loadCategories,
    categories,
    history,
    theme,
    displayPricingAndInventoryToggles,
    isCompactHeaderFlyout,
    showFlyoutPricingToggle,
    showFlyoutInventoryToggle,
    rootElementSelector,
    flyoutDrawerIsOpen,
    navDrawerIsOpen,
    setSignInOverlayIsOpen,
    visibilityState,
    allowOpenParentLinks,
    signInUrl,
}: Props) => {
    const showIcon = visibilityState === "both" || visibilityState === "icon";
    const showLabel = visibilityState === "both" || visibilityState === "label";

    const headerLinkListLinks = useGetLinks(headerLinkListLinkFields, o => o.fields.destination);
    const dispatch = useDispatch();

    React.useEffect(() => {
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories();
        }
    }, []);

    const signInPanelClickHandler = (event: React.MouseEvent) => {
        if (theme.signIn?.useOverlay) {
            event.preventDefault();
            setSignInOverlayIsOpen({ isOpen: true });
        }

        closeDrawer();
    };

    const signInPanelKeyPressHandler = (event: React.KeyboardEvent) => {
        if (event.key === " " && history && signInUrl?.url) {
            event.preventDefault();

            if (theme.signIn?.useOverlay) {
                setSignInOverlayIsOpen({ isOpen: true });
            } else {
                history.push(signInUrl.url);
            }

            closeDrawer();
        }
    };

    const closeDrawer = () => {
        if (isCompactHeaderFlyout) {
            setFlyoutDrawerIsOpen({ isOpen: false });
            setTimeout(() => setFlyoutDrawerIsOpen({ isOpen: undefined }), 300);
            return;
        }

        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
    };

    const handlePageClick = (e: any, url?: string) => {
        if (url) {
            e.stopPropagation();
            history.push(url);
            closeDrawer();
        }
    };

    const currentPageUrl = currentLocation.pathname;

    React.useEffect(() => {
        const tempStateCategories: CategoryModel[] = [];
        // l1links
        const tempCategories: any = [];
        for (let i = 0; i < headerShopByCatLinks.length; i++) {
            const tCat = headerShopByCatLinks[i];

            const {
                fields: { destination, overrideTitle },
            } = tCat;
            const { type, value } = destination;

            let child = {};

            if (type === "Url") {
                child = {
                    url: value,
                    title: value,
                    overrideTitle,
                };
            } else if (type === "Page") {
                const readonlyPageLink = getPageLinkByNodeId(state, value);
                if (readonlyPageLink) {
                    child = { ...readonlyPageLink, overrideTitle };
                }
            } else if (type === "Category") {
                const categoryState = getCategoryState(state, value);

                child = {
                    url: categoryState && categoryState.value ? categoryState.value.path : "",
                    title: categoryState && categoryState.value ? categoryState.value.shortDescription : "",
                    overrideTitle,
                };
            }

            if (!child) {
                child = {
                    url: "",
                    title: "",
                };
            }

            tempCategories.push(child);
        }

        // l2 links
        for (let i = 0; i < tempCategories.length; i++) {
            const tCat = tempCategories[i];
            const link = { url: tCat.url, title: tCat.title };
            const overrideTitle = tCat.overrideTitle;
            const children: any = [];
            const excludeFromNavigation = tCat?.excludeFromNavigation || false;
            if (headerShopByCatLinks[i].fields.l2Links.length > 0) {
                for (const link2 of headerShopByCatLinks[i].fields.l2Links) {
                    const {
                        fields: { destination, overrideTitleL2 },
                    } = link2;
                    const { type, value } = destination;

                    let child = {};

                    if (type === "Url") {
                        child = {
                            url: value,
                            title: value,
                            overrideTitleL2,
                        };
                    } else if (type === "Page") {
                        const readonlyPageLink = getPageLinkByNodeId(state, value);
                        if (readonlyPageLink) {
                            child = { ...readonlyPageLink, overrideTitleL2 };
                        }
                    } else if (type === "Category") {
                        const categoryState = getCategoryState(state, value);

                        child = {
                            url: categoryState && categoryState.value ? categoryState.value.path : "",
                            title: categoryState && categoryState.value ? categoryState.value.shortDescription : "",
                            overrideTitleL2,
                        };
                    }

                    if (!child) {
                        continue;
                    }

                    children.push(child);
                }
            }
            const catToPush = {
                link,
                children,
                overrideTitle,
                excludeFromNavigation,
            };
            tempStateCategories.push(catToPush);
        }

        const viewAllCatsString = translate("View All Categories");
        const allCatsPage: any = {
            link: { title: viewAllCatsString, url: categoriesPageUrl },
            children: [],
        };

        tempStateCategories.push(allCatsPage);

        // store the header links in redux so that we use them here in this component and in the ShopByCategoryL1L2 component
        dispatch({ type: "xHeader/setHeaderLinks", headerLinks: tempStateCategories });
    }, [headerShopByCatLinks, categoriesDataView?.value]);

    const isOpen = isCompactHeaderFlyout ? flyoutDrawerIsOpen : navDrawerIsOpen;

    return (
        <>
            <Drawer
                className="drawer"
                draggable={false}
                position="left"
                {...styles.drawer}
                isOpen={isOpen}
                handleClose={closeDrawer}
                contentLabel="menu drawer"
                data-test-selector="drawerSection"
                rootElementSelector={rootElementSelector}
            >
                {showShopByCategory && categories.length > 0 && (
                    <StyledSection>
                        <PanelMenu
                            key="shop-by-categoryIndex"
                            data-test-selector="shop-by-categoryIndex"
                            currentUrl={currentPageUrl}
                            closeOverlay={closeDrawer}
                            panelTrigger={
                                <PanelRow hasChildren {...styles.mainNavigationRow}>
                                    <Typography {...styles.mainNavigationRowTypography}>
                                        {translate("Shop by Category")}
                                    </Typography>
                                </PanelRow>
                            }
                            layer={0}
                            {...styles.panelMenu}
                        >
                            <StyledSection>
                                {categories.map((category: CategoryModel, index) => {
                                    if (
                                        category.children &&
                                        category.children.length > 0 &&
                                        ((category as any)?.excludeFromNavigation === false ||
                                            (category as any)?.excludeFromNavigation === undefined)
                                    ) {
                                        return (
                                            <PanelMenu
                                                // eslint-disable-next-line react/no-array-index-key
                                                key={`${category.overrideTitle || category.link.title}_${index}_parent`}
                                                currentUrl={currentPageUrl}
                                                closeOverlay={closeDrawer}
                                                panelTrigger={
                                                    <PanelRow hasChildren {...styles.mainNavigationRow}>
                                                        <Typography
                                                            {...styles.mainNavigationRowTypography}
                                                            onClick={(e: any) => handlePageClick(e, category.link.url)}
                                                        >
                                                            {category.overrideTitle || category.link.title}
                                                        </Typography>
                                                    </PanelRow>
                                                }
                                                layer={0}
                                                allowOpenParentLinks={allowOpenParentLinks}
                                                {...styles.panelMenu}
                                            >
                                                {category.children.map((l2Cat, index) => {
                                                    if (
                                                        l2Cat.url &&
                                                        l2Cat.title &&
                                                        ((l2Cat as any)?.excludeFromNavigation === false ||
                                                            (l2Cat as any)?.excludeFromNavigation === undefined)
                                                    ) {
                                                        return (
                                                            <PanelRow
                                                                // eslint-disable-next-line react/no-array-index-key
                                                                key={`${
                                                                    l2Cat.overrideTitleL2 || l2Cat.title
                                                                }_${index}_level2`}
                                                                href={l2Cat.url}
                                                                {...styles.mainNavigationRow}
                                                                onClick={closeDrawer}
                                                            >
                                                                <Typography {...styles.mainNavigationRowTypography}>
                                                                    {l2Cat.overrideTitleL2 || l2Cat.title}
                                                                </Typography>
                                                            </PanelRow>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                            </PanelMenu>
                                        );
                                    }
                                    if (
                                        (category as any)?.excludeFromNavigation === false ||
                                        (category as any)?.excludeFromNavigation === undefined
                                    ) {
                                        return (
                                            <PanelRow
                                                // eslint-disable-next-line react/no-array-index-key
                                                key={`${category.overrideTitle || category.link.title}_${index}`}
                                                href={category.link.url}
                                                {...styles.mainNavigationRow}
                                                onClick={closeDrawer}
                                            >
                                                <Typography {...styles.mainNavigationRowTypography}>
                                                    {category.overrideTitle || category.link.title}
                                                </Typography>
                                            </PanelRow>
                                        );
                                    }
                                    return null;
                                })}
                            </StyledSection>
                        </PanelMenu>
                    </StyledSection>
                )}
                {showBrandsAsOverlay && featuredBrands && featuredBrands.length > 0 && (
                    <StyledSection>
                        <PanelMenu
                            currentUrl={currentPageUrl}
                            closeOverlay={closeDrawer}
                            panelTrigger={
                                <PanelRow hasChildren {...styles.mainNavigationRow}>
                                    <Typography {...styles.mainNavigationRowTypography}>
                                        {translate("Brands")}
                                    </Typography>
                                </PanelRow>
                            }
                            layer={0}
                            allowOpenParentLinks={allowOpenParentLinks}
                            {...styles.panelMenu}
                        >
                            <StyledSection>
                                {featuredBrands.map((brand, index) => {
                                    return (
                                        <PanelRow
                                            key={`${brand.id || brand.name}_${index}`}
                                            href={brand.detailPagePath}
                                            {...styles.mainNavigationRow}
                                            onClick={closeDrawer}
                                        >
                                            <Typography {...styles.mainNavigationRowTypography}>
                                                {brand.name}
                                            </Typography>
                                        </PanelRow>
                                    );
                                })}
                            </StyledSection>
                        </PanelMenu>
                    </StyledSection>
                )}
                {showBrandsAsOverlay && featuredBrands && featuredBrands?.length < 1 && brandsPageUrl && (
                    <PanelRow
                        isCurrent={currentPageUrl === brandsPageUrl.url}
                        onClick={closeDrawer}
                        href={brandsPageUrl.url}
                        {...styles.mainNavigationRow}
                    >
                        <Typography {...styles.mainNavigationRowTypography}>{brandsPageUrl.title}</Typography>
                    </PanelRow>
                )}

                <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                    {/* covers MainNavigation functionality */}
                    {links.map((link, index) => {
                        if (
                            (link as any)?.excludeFromNavigation === false ||
                            (link as any)?.excludeFromNavigation === undefined
                        ) {
                            return (
                                <PanelRow
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`${link.title}_${index}`}
                                    className="main-navigation-row"
                                    href={link.url === currentPageUrl ? undefined : link.url}
                                    isCurrent={link.url === currentPageUrl}
                                    target={link.openInNewWindow ? "_blank" : ""}
                                    onClick={closeDrawer}
                                    allowOpenParentLinks={allowOpenParentLinks}
                                    {...styles.mainNavigationRow}
                                >
                                    <Typography {...styles.mainNavigationRowTypography}>{link.title}</Typography>
                                </PanelRow>
                            );
                        }
                        return null;
                    })}
                    {showQuickOrder && quickOrderLink && (
                        <PanelRow
                            className="main-navigation-row"
                            isCurrent={currentPageUrl === quickOrderLink.url}
                            onClick={closeDrawer}
                            href={quickOrderLink.url}
                            {...styles.mainNavigationRow}
                        >
                            <Typography className="main-navigation-row-text" {...styles.mainNavigationRowTypography}>
                                {quickOrderLink.title}
                            </Typography>
                        </PanelRow>
                    )}
                </StyledSection>
                {headerLinkListLinks.length > 0 && (
                    <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                        {headerLinkListLinks.map(
                            (link, index) =>
                                link.url &&
                                link.title && (
                                    <PanelRow
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={`${
                                            headerLinkListLinkFields[index]?.fields?.overriddenTitle || link.title
                                        }_${index}`}
                                        {...(styles.logoLinks && omitSingle(styles.logoLinks, "typographyProps"))}
                                        isCurrent={currentPageUrl === link.url}
                                        className="logo-links-row"
                                        onClick={closeDrawer}
                                        href={link.url}
                                        target={headerLinkListLinkFields[index].fields.openInNewWindow ? "_blank" : ""}
                                    >
                                        <Typography className="logo-link-title" {...styles.logoLinks?.typographyProps}>
                                            {headerLinkListLinkFields[index]?.fields?.overriddenTitle || link.title}
                                        </Typography>
                                    </PanelRow>
                                ),
                        )}
                    </StyledSection>
                )}
                <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                    <StyledWrapper className="currency-selector-wrapper" {...styles.currencySelectorWrapper}>
                        <SelectorMenu
                            options={currencies?.map(c => {
                                return {
                                    title: c.currencyCode.toUpperCase(),
                                    clickableProps: {
                                        onClick: () => {
                                            setCurrency({ currencyId: c.id });
                                            closeDrawer();
                                        },
                                    },
                                };
                            })}
                            closeModal={closeDrawer}
                            currentOption={currencies?.find(c => c.id === currentCurrencyId)?.currencyCode}
                            currentOptionIcon={
                                <Typography {...styles.currencySymbol}>{currentCurrencySymbol}</Typography>
                            }
                        />
                    </StyledWrapper>
                    <StyledWrapper className="language-selector-wrapper" {...styles.languageSelectorWrapper}>
                        <SelectorMenu
                            dataTestSelector="mobileLanguageSelector"
                            options={languages?.map(l => {
                                return {
                                    title: l.languageCode.toUpperCase(),
                                    clickableProps: {
                                        onClick: () => {
                                            setLanguage({ languageId: l.id });
                                            closeDrawer();
                                        },
                                    },
                                };
                            })}
                            currentOption={currentLanguage?.languageCode}
                            closeModal={closeDrawer}
                            currentOptionIcon={
                                currentLanguage?.imageFilePath ? (
                                    <LogoImage className="language-image" src={currentLanguage.imageFilePath} alt="" />
                                ) : (
                                    <Icon className="menu-row-icon" src="Globe" {...styles.menuRowIcon} />
                                )
                            }
                        />
                    </StyledWrapper>
                </StyledSection>

                <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                    <PanelRow {...styles.headerShipToAddressStyles}>
                        <Typography>
                            {createWidgetElement("Common/HeaderShipToAddress", {
                                fields: {},
                            })}
                        </Typography>
                    </PanelRow>
                </StyledSection>
                {displayPricingAndInventoryToggles && (
                    <>
                        {(!isCompactHeaderFlyout || showFlyoutInventoryToggle) && (
                            <ShowInventoryToggle extendedStyles={styles.showInventoryToggle} />
                        )}
                        {(!isCompactHeaderFlyout || showFlyoutPricingToggle) && (
                            <ShowPricingToggle extendedStyles={styles.showPricingToggle} />
                        )}
                    </>
                )}
                {!isAuth && (
                    <StyledSection
                        css={css`
                            padding: 10px 20px;
                        `}
                    >
                        <PostalCode fulfillmentMethod={fulfillmentMethod} />
                    </StyledSection>
                )}
            </Drawer>
        </>
    );
};

const LogoImage = styled.img`
    margin-right: 10px;
    height: 22px;
`;

interface SelectorMenuProps {
    options?: MappedLink[];
    closeModal?: () => void;
    currentOption?: string;
    currentOptionIcon?: React.ReactNode;
    dataTestSelector?: string;
}

const SelectorMenu: React.FC<SelectorMenuProps> = ({
    options,
    closeModal,
    currentOption,
    currentOptionIcon,
    dataTestSelector,
}) => {
    if (!options || options.length <= 1) {
        return null;
    }

    const trigger = (
        <PanelRow className="logo-links-row" hasChildren {...styles.logoLinks}>
            <StyledSpan className="panel-section-wrapper" {...styles.panelSectionWrapper}>
                {currentOptionIcon}
                <Typography
                    className="logo-link-title"
                    data-test-selector={`${dataTestSelector}_currentOption`}
                    {...styles.logoLinks?.typographyProps}
                >
                    {currentOption}
                </Typography>
            </StyledSpan>
        </PanelRow>
    );

    return (
        <PanelMenu
            data-test-selector={dataTestSelector}
            className="panel-menu"
            currentUrl={currentOption}
            closeOverlay={closeModal}
            panelTrigger={trigger}
            menuItems={options}
            maxDepth={1}
            layer={0}
            {...styles.panelMenu}
        />
    );
};

function findVisibilityState(data: any): string | undefined {
    for (const key in data) {
        if (data[key].type === "Common/HeaderSignIn") {
            return data[key].generalFields.visibilityState;
        }
    }
    return undefined;
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(NavigationDrawer)));
